import { makeAutoObservable } from 'mobx';
import { CanvasUtils } from '../utils/CanvasUtils';
import { Utils } from '../utils/Utils';
import { ClosetMode, DrawingMode, ButtonMode } from '../config';
import RBush from 'rbush';
import { message } from 'antd';
import {
    DEFAULT_INTERNAL_WALL_LENGTH,
    DEFAULT_INTERNAL_WALL_WIDTH,
    SCALEFACTOR_FOR_CONVERSION,
} from '../constant';

export class DrawingManager {
    _libState = null;
    _drawingMode = DrawingMode.NONE;
    _liveLine = false;
    _closetPolygon = [];
    _currentPoint = null;
    _snapThreshold = 10;
    _scaleFactor = 0.25;
    _annotationCanvas = null;
    _pointsToSnapFrom = [];
    spatialIndex = new RBush();
    _shouldSnap = true;
    _drawingForm = false;
    _closetOut = false;
    _rectangleMode = false;
    _rectangleStartPoint = null;

    constructor(libState) {
        this._libState = libState;
        makeAutoObservable(this);
    }

    get isDrawingModeOn() {
        return (
            this._drawingMode === DrawingMode.DRAW ||
            this._drawingMode === DrawingMode.UNIT
        );
    }
    get isUnitModeOn() {
        return this._drawingMode === DrawingMode.UNIT;
    }

    get isDoorModeOn() {
        return this._drawingMode === DrawingMode.DOOR;
    }

    get isInternalWallModeOn() {
        return this._drawingMode === DrawingMode.INTERNAL_WALL;
    }
    get isCalibrationModeOn() {
        return this._drawingMode === DrawingMode.CALIBRATION;
    }

    get isDeleteMode() {
        return this._libState.closetManager.closetMode === ClosetMode.DELETE;
    }

    get closetPolygon() {
        return this._closetPolygon;
    }

    get annotationCanvas() {
        return this._annotationCanvas;
    }

    get pointsToSnapFrom() {
        return this._pointsToSnapFrom;
    }
    get drawingForm() {
        return this._drawingForm;
    }

    setDrawingForm(drawingForm) {
        this._drawingForm = drawingForm;
    }

    get shouldSnap() {
        return this._shouldSnap;
    }

    get closetOut() {
        return this._closetOut;
    }

    setClosetOut(closetOut) {
        this._closetOut = closetOut;
    }

    setShouldSnap(shouldSnap) {
        this._shouldSnap = shouldSnap;
    }

    setAnnotationCanvas(annotationCanvas) {
        this._annotationCanvas = annotationCanvas;
    }

    setDrawingMode = (drawingModeBool) => {
        this._libState.closetManager.setDistanceLabel(false);
        this._drawingMode = drawingModeBool
            ? DrawingMode.DRAW
            : DrawingMode.NONE;
        if (this._drawingMode === DrawingMode.DRAW) {
            this.prepareCanvasForDrawing();
        }
        if (this._libState.closetManager._closetMode != ClosetMode.EDIT) {
            this._libState.closetManager._closetMode = ClosetMode.EDIT;
            this._libState.closetManager.setDistanceLabel(true);
            this._libState.closetManager.drawNewClosets();
        }
    };

    setCalibrationMode = (calibrationModeBool) => {
        calibrationModeBool
            ? (this._drawingMode = DrawingMode.CALIBRATION)
            : (this._drawingMode = DrawingMode.NONE);

        if (this._drawingMode === DrawingMode.CALIBRATION) {
            this.prepareCanvasForDrawing();
        }
        if (this._libState.closetManager._closetMode != ClosetMode.EDIT) {
            this._libState.closetManager._closetMode = ClosetMode.EDIT;
            this._libState.closetManager.setDistanceLabel(true);
            this._libState.closetManager.drawNewClosets();
        }
    };

    setDoorMode = (drawingModeBool) => {
        this._libState.closetManager.setDistanceLabel(false);
        this._drawingMode = drawingModeBool
            ? DrawingMode.DOOR
            : DrawingMode.NONE;
        if (this._drawingMode === DrawingMode.DOOR) {
            this.prepareCanvasForDrawing();
        }
        if (this._libState.closetManager.closetMode != ClosetMode.EDIT) {
            this._libState.closetManager.setEditMode();
            this._libState.closetManager.setDistanceLabel(true);
            this._libState.closetManager.drawNewClosets();
        }
    };

    setInternalWallMode = (drawingModeBool) => {
        this._libState.closetManager.setDistanceLabel(false);
        this._drawingMode = drawingModeBool
            ? DrawingMode.INTERNAL_WALL
            : DrawingMode.NONE;
        if (this._drawingMode === DrawingMode.INTERNAL_WALL) {
            this.prepareCanvasForDrawing();
        }
        if (this._libState.closetManager.closetMode != ClosetMode.EDIT) {
            this._libState.closetManager.setEditMode();
            this._libState.closetManager.setDistanceLabel(true);
            this._libState.closetManager.drawNewClosets();
        }
    };

    setUnitMode = (drawingModeBool) => {
        this._libState.closetManager.setDistanceLabel(false);
        this._drawingMode = drawingModeBool
            ? DrawingMode.UNIT
            : DrawingMode.NONE;
        if (this._drawingMode === DrawingMode.UNIT) {
            this.prepareCanvasForDrawing();
        }
        if (this._libState.closetManager.closetMode != ClosetMode.EDIT) {
            this._libState.closetManager.setEditMode();
            this._libState.closetManager.setDistanceLabel(true);
            this._libState.closetManager.drawNewClosets();
        }
    };

    scaleDrawnPolygon(scaleFactor) {
        this._closetPolygon = this.closetPolygon.map((point) => ({
            x: point.x / scaleFactor,
            y: point.y / scaleFactor,
        }));
        this.prepareCanvasForDrawing();
        this.drawOnCanvas();
        this.drawStaticLines();
        this.setCurrentPoint(
            this._closetPolygon[this.closetPolygon.length - 1],
        );
    }

    setPointsToSnapFrom(points) {
        this._pointsToSnapFrom = points;
        this.buildSpatialIndex(); // Precompute spatial indexing
    }

    saveCloset = () => {
        const closetPolygon = this.closetPolygon;
        const id = Utils.generateUuid();
        this._libState.closetManager.addCloset(id, closetPolygon);
        this._closetPolygon = [];
        this.setDrawingMode(false);
        this.setLiveLine(false);
        this.setCurrentPoint(null);
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
        this._libState.closetManager.setSelectedClosetById(id);
        this._libState.viewManager.setClosetForm(true);
        this._libState.closetManager.setDistanceLabel(true);
        this._libState.closetManager.drawNewClosets();
    };

    saveUnit = () => {
        const closetPolygon = this.closetPolygon;
        const id = Utils.generateUuid();
        this._libState.closetManager.addUnit(id, closetPolygon);
        this._closetPolygon = [];
        this.setDrawingMode(false);
        this.setLiveLine(false);
        this.setCurrentPoint(null);
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
        this._libState.closetManager.setSelectedUnitById(id);
        this._libState.viewManager.setUnitForm(true);
        this._libState.closetManager.setDistanceLabel(true);
        this._libState.closetManager.drawNewClosets();
    };

    saveDoor = () => {
        const closetPolygon = this.closetPolygon;
        const id = Utils.generateUuid();
        const closetId =
            this._libState.closetManager.fetchClosetIdByAttachmentCoordinates(
                closetPolygon,
                this._libState.closetManager.selectedPageNo,
            );
        this._libState.closetManager.addAttachment(id, closetId, closetPolygon);
        this._closetPolygon = [];
        this.setDrawingMode(false);
        this.setLiveLine(false);
        this.setCurrentPoint(null);
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
        this._libState.closetManager.setDistanceLabel(true);
        this._libState.closetManager.drawNewClosets();
    };

    saveCalibrationLine = () => {
        const closetPolygon = this.closetPolygon;
        this._libState.closetManager.addCalibrationLine(closetPolygon);
        this._closetPolygon = [];
        this.setDrawingMode(false);
        this.setLiveLine(false);
        this.setCurrentPoint(null);
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
        this._libState.closetManager.setDistanceLabel(true);
        this._libState.closetManager.drawNewClosets();
    };

    clearAnnotationCanvas = () => {
        const context = this.annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        context.clearRect(
            0,
            0,
            context.canvas.width - transX,
            context.canvas.height - transY,
        );
    };

    setCurrentPoint = (currentPoint) => {
        this._currentPoint = currentPoint;
    };

    prepareCanvasForDrawing = () => {
        this.annotationCanvas.width =
            this._libState.closetManager.pdfCanvasRef.width;
        this.annotationCanvas.height =
            this._libState.closetManager.pdfCanvasRef.height;
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
    };

    get currentPoint() {
        return this._currentPoint;
    }

    get liveLine() {
        return Boolean(
            this._liveLine &&
                (this._drawingMode === DrawingMode.DRAW ||
                    this._drawingMode === DrawingMode.DOOR ||
                    this._drawingMode === DrawingMode.UNIT ||
                    this._drawingMode === DrawingMode.CALIBRATION),
        );
    }

    get snapThreshold() {
        return this._snapThreshold;
    }

    setSnapThreshold = (snapThreshold) => {
        this._snapThreshold = snapThreshold;
    };

    setLiveLine = (liveLineBool) => {
        this._liveLine = liveLineBool;
    };

    addPointToClosetPolygon = (point) => {
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;

        const annotationCanvasRect =
            this.annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        let isInside = false;
        const page = this._libState.closetManager.selectedPageNo;

        this._libState.closetManager.units.forEach((closet) => {
            const closetPolygon = closet.coordinates;
            if (
                closet.pageNo === page &&
                Utils.pointInPolygon(point, closetPolygon)
            ) {
                isInside = true;
            }
        });

        if (!isInside) return message.error('Point is outside the unit');

        this.setLiveLine(true);

        let snappedPoint = this.findNearestPoint(point, this.snapThreshold);
        if (!snappedPoint) {
            snappedPoint = this.snapPointToPolygon(point);
        }
        if (this._closetPolygon.length > 0) {
            const lastPoint =
                this._closetPolygon[this._closetPolygon.length - 1];
            snappedPoint = this.roundDistanceToWholeNumber(
                lastPoint,
                snappedPoint,
            );
        }

        if (
            this._closetPolygon.length > 0 &&
            Utils.isPointInTol(
                snappedPoint,
                this.closetPolygon[this.closetPolygon.length - 1],
                1,
            )
        ) {
            this.revertClosetPolygon(1);
            return;
        }

        if (
            this._closetPolygon.length == 2 &&
            Utils.isPointInTol(snappedPoint, this._closetPolygon[0], 5)
        ) {
            this.revertClosetPolygon(1);
            return;
        }

        if (this._closetPolygon.length > 2) {
            const firstPoint = this._closetPolygon[0];
            const distanceToFirst = Math.hypot(
                snappedPoint.x - firstPoint.x,
                snappedPoint.y - firstPoint.y,
            );

            if (distanceToFirst < this.snapThreshold) {
                snappedPoint.x = firstPoint.x;
                snappedPoint.y = firstPoint.y;
                this.setLiveLine(false);
                this.setDrawingMode(false);
                if (this.checkIsSelfIntersecting()) return;
                if (this.checkAreClosetIntersecting()) return;
                if (this.closetOut) {
                    this.setClosetOut(false);
                    this.setLiveLine(true);
                    this.setDrawingMode(true);
                    this.setCurrentPoint(
                        this._closetPolygon[this._closetPolygon.length - 1],
                    );
                    message.error('Closet is outside the unit');
                    this.drawOnCanvas();
                    this.drawStaticLines();
                    return;
                }
                this.saveCloset();
                return;
            }
        }

        this._closetPolygon.push(snappedPoint);
        this.setCurrentPoint(snappedPoint);
        this.drawOnCanvas();
    };

    checkIsSelfIntersecting = () => {
        if (this._libState.viewManager.buttonMode === ButtonMode.Closet) {
            if (this._closetPolygon.length < 2) return;
            let isInside = this._libState.closetManager.checkClosetInsideUnit(
                this._closetPolygon,
                this.liveLine ? 1 : 0,
            );
            if (
                Utils.isSelfIntersect(
                    this._closetPolygon,
                    this.liveLine ? 1 : 0,
                ) ||
                !isInside
            ) {
                if (!this.liveLine) {
                    this.setLiveLine(true);
                    this.setDrawingMode(true);
                    this.setCurrentPoint(
                        this._closetPolygon[this._closetPolygon.length - 1],
                    );
                    message.error('Intersecting points');
                    this.drawOnCanvas();
                    this.drawStaticLines();
                    return true;
                }
                this._closetPolygon.pop();
                this.setCurrentPoint(
                    this._closetPolygon[this._closetPolygon.length - 1],
                );
                message.error('Intersecting points');
                return true;
            }
            if (!this.liveLine) this.setClosetOut(false);
        } else if (this._libState.viewManager.buttonMode === ButtonMode.Unit) {
            if (this._closetPolygon.length < 2) return false;
            if (
                Utils.isSelfIntersect(
                    this._closetPolygon,
                    this.liveLine ? 1 : 0,
                )
            ) {
                this._closetPolygon.pop();
                this.setCurrentPoint(
                    this._closetPolygon[this._closetPolygon.length - 1],
                );
                message.error('Intersecting points');
                return true;
            }
        }
        return false;
    };

    checkAreUnitsIntersecting = () => {
        if (
            this._libState.closetManager.unitIntersectionForIncompleteUnits(
                this.closetPolygon,
                0,
            )
        ) {
            this.setLiveLine(true);
            this.setUnitMode(true);
            this.setCurrentPoint(
                this._closetPolygon[this._closetPolygon.length - 1],
            );
            this.drawOnCanvas();
            this.drawStaticLines();
            message.error('Unit intersects with another unit.');
            return true;
        }
        return false;
    };

    checkAreClosetIntersecting = () => {
        if (
            this._libState.closetManager.closetIntersectionForIncompleteClosets(
                this.closetPolygon,
                0,
            )
        ) {
            this.setLiveLine(true);
            this.setDrawingMode(true);
            this.setCurrentPoint(
                this._closetPolygon[this._closetPolygon.length - 1],
            );
            this.drawOnCanvas();
            this.drawStaticLines();
            message.error('Closet intersects with another closet.');
            return true;
        }
        return false;
    };

    revertClosetPolygon = (num = 0) => {
        this._closetPolygon.pop();
        this.setCurrentPoint(
            this._closetPolygon[this._closetPolygon.length - 1],
        );
        if (num === 1) {
            message.success('Undo: last sketch point removed.');
        }
        if (
            this._libState.viewManager.buttonMode === ButtonMode.Closet &&
            num === 0
        ) {
            message.error('Closet intersects with another closet.');
        } else if (
            this._libState.viewManager.buttonMode === ButtonMode.Unit &&
            num === 0
        ) {
            message.error('Unit intersects with another unit.');
        }
    };

    addPointToClosetPolygonForDoor = (point) => {
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        if (this._libState.closetManager.closets.length === 0) {
            this.setDoorMode(false);
            this.setDrawingForm(false);
            return;
        }
        this.setLiveLine(true);
        let bool = false;
        this._closetPolygon.length == 1 ? (bool = false) : (bool = true);

        const annotationCanvasRect =
            this.annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        let snappedPoint = this._libState.closetManager.nearestClosetPoint(
            point,
            this.snapThreshold,
            bool,
        );
        if (!snappedPoint) {
            return;
        }
        const closetId =
            this._libState.closetManager.fetchClosetIdByAttachmentCoordinates(
                [snappedPoint],
                this._libState.closetManager.selectedPageNo,
            );
        if (this._closetPolygon.length > 0) {
            const lastPoint =
                this._closetPolygon[this._closetPolygon.length - 1];
            snappedPoint = this.roundDistanceToWholeNumber(
                lastPoint,
                snappedPoint,
            );
        }
        const edgePoints = this._libState.closetManager.selectedEdgeEnds;
        if (!Utils.isPointOnLine(snappedPoint, edgePoints[0], edgePoints[1])) {
            message.error('Point is not on the edge.');
            return;
        }
        if (this._libState.closetManager.doorIntersectionCheck(snappedPoint)) {
            message.error('Door intersects with another door.');
            return;
        }
        if (this._closetPolygon.length == 1) {
            if (
                this._libState.closetManager.doorOverlapCheck(
                    this._closetPolygon[0],
                    snappedPoint,
                )
            ) {
                message.error('Door intersects with another door.');
                return;
            }
            const scale =
                this._libState.closetManager.getScaleFactorForAnnotationDisplayByClosetId(
                    this._libState.closetManager.selectedPageNo,
                    closetId,
                );
            const doorLength = CanvasUtils.measureDistance(
                this._closetPolygon[0],
                snappedPoint,
                scale,
            );
            if (doorLength < 10) {
                message.error('Door is too short.');
                return;
            }
            this._closetPolygon.push(snappedPoint);
            this.setLiveLine(false);
            this.setDoorMode(false);
            this.saveDoor();
            return;
        }

        this._closetPolygon.push(snappedPoint);
        this.setCurrentPoint(snappedPoint);
        this.drawOnCanvas();
    };

    addPointToClosetPolygonForInternalWall = () => {
        if (this._libState.closetManager.closets.length === 0) {
            this.setInternalWallMode(false);
            this.setDrawingForm(false);
            return;
        }
        // const annotationCanvasRect =
        //     this.annotationCanvas.getBoundingClientRect();
        // point.x -= annotationCanvasRect.left + transX;
        // point.y -= annotationCanvasRect.top + transY;

        // let snappedPoint = this._libState.closetManager.nearestClosetPoint(
        //     point,
        //     50,
        //     false,
        // );
        // if (!snappedPoint) {
        //     return;
        // }
        this.setInternalWallMode(false);
        const id = Utils.generateUuid();
        this._libState.closetManager.addInternalWall(id);
        this.setDrawingMode(false);
        this.setCurrentPoint(null);
        this.clearAnnotationCanvas();
        this.transformAnnotationCanvas();
        this._libState.closetManager.setInternalForm(true);
        this._libState.closetManager.setDistanceLabel(true);
        this._libState.closetManager.drawNewClosets();
    };

    addPointToClosetPolygonForCalibration = (point) => {
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        this.setLiveLine(true);

        const annotationCanvasRect =
            this.annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;
        let snappedPoint = this.findNearestPoint(point, this.snapThreshold);
        if (!snappedPoint) {
            snappedPoint = this.snapPointToPolygon(point);
        }

        if (this._closetPolygon.length > 0) {
            const lastPoint =
                this._closetPolygon[this._closetPolygon.length - 1];
            snappedPoint = this.roundDistanceToWholeNumber(
                lastPoint,
                snappedPoint,
            );
        }

        if (this._closetPolygon.length == 1) {
            this._closetPolygon.push(snappedPoint);
            this.setLiveLine(false);
            this.setCalibrationMode(false);
            this.saveCalibrationLine();
            return;
        }

        this._closetPolygon.push(snappedPoint);
        this.setCurrentPoint(snappedPoint);
        this.drawOnCanvas();
    };

    addPointToClosetPolygonForUnit = (point) => {
        this.setLiveLine(true);
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;

        const annotationCanvasRect =
            this.annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        let snappedPoint = this.findNearestPoint(point, this.snapThreshold);
        if (!snappedPoint) {
            snappedPoint = this.snapPointToPolygon(point);
        }

        if (this._closetPolygon.length > 0) {
            const lastPoint =
                this._closetPolygon[this._closetPolygon.length - 1];
            snappedPoint = this.roundDistanceToWholeNumber(
                lastPoint,
                snappedPoint,
            );
        }

        if (
            this._closetPolygon.length > 0 &&
            Utils.isPointInTol(
                snappedPoint,
                this.closetPolygon[this.closetPolygon.length - 1],
                1,
            )
        ) {
            this.revertClosetPolygon(1);
            return;
        }

        if (
            this._closetPolygon.length == 2 &&
            Utils.isPointInTol(snappedPoint, this._closetPolygon[0], 5)
        ) {
            this.revertClosetPolygon(1);
            return;
        }

        if (this._closetPolygon.length > 2) {
            const firstPoint = this._closetPolygon[0];
            const distanceToFirst = Math.hypot(
                snappedPoint.x - firstPoint.x,
                snappedPoint.y - firstPoint.y,
            );

            if (distanceToFirst < this.snapThreshold) {
                snappedPoint.x = firstPoint.x;
                snappedPoint.y = firstPoint.y;
                this.setLiveLine(false);
                if (this.checkAreUnitsIntersecting()) return;
                this.setDrawingMode(false);
                this.saveUnit();
                return;
            }
        }

        this._closetPolygon.push(snappedPoint);
        this.setCurrentPoint(snappedPoint);
        this.drawOnCanvas();
    };

    snapPointToPolygon = (point) => {
        const tol = this.snapThreshold;
        if (this.currentPoint) {
            const dx = Math.abs(point.x - this.currentPoint.x);
            const dy = Math.abs(point.y - this.currentPoint.y);

            if (dx < tol) point.x = this.currentPoint.x;
            if (dy < tol) point.y = this.currentPoint.y;
        }

        if (this._closetPolygon.length >= 3) {
            const firstPoint = this._closetPolygon[0];
            const alignX = Math.abs(point.x - firstPoint.x) < tol;
            const alignY = Math.abs(point.y - firstPoint.y) < tol;

            if (alignX) point.x = firstPoint.x;
            if (alignY) point.y = firstPoint.y;
        }

        return point;
    };

    buildSpatialIndex() {
        const items = this._pointsToSnapFrom.map((pt) => ({
            minX: pt.x,
            maxX: pt.x,
            minY: pt.y,
            maxY: pt.y,
            point: pt,
        }));
        this.spatialIndex.clear();
        this.spatialIndex.load(items);
    }

    findNearestPoint(target, maxDist) {
        if (this.shouldSnap === false) return null;
        const currentPageScale =
            this._libState.closetManager.fetchTransformationScaleFactorByPageNo(
                this._libState.closetManager.selectedPageNo,
            );
        const targetY = target.y / currentPageScale;
        const targetX = target.x / currentPageScale;

        const searchArea = {
            minX: targetX - maxDist,
            maxX: targetX + maxDist,
            minY: targetY - maxDist,
            maxY: targetY + maxDist,
        };
        const results = this.spatialIndex.search(searchArea);
        if (results.length === 0) {
            console.warn('No points found in the tree');
            return null;
        }

        let nearestPoint = null;
        let minDist = Infinity;

        results.forEach((item) => {
            const pt = item.point;
            // pt.x /= this._libState.closetManager.scaleOnPdf;
            // pt.y /= this._libState.closetManager.scaleOnPdf;
            const dist = Math.hypot(
                target.x - pt.x * currentPageScale,
                target.y - pt.y * currentPageScale,
            );

            if (dist < minDist && dist <= maxDist) {
                minDist = dist;
                nearestPoint = pt;
            }
        });
        if (!nearestPoint) return null;
        let newNp = { ...nearestPoint };

        if (nearestPoint) {
            newNp.x = nearestPoint.x * currentPageScale;
            newNp.y = nearestPoint.y * currentPageScale;
        }

        return newNp;
    }

    drawOnCanvas = () => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');

        // Redraw all the circles after clearing
        this._closetPolygon.forEach((pt) => {
            CanvasUtils.drawCircle(context, pt, 2);
        });
    };

    transformAnnotationCanvas = () => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;

        context.setTransform(1, 0, 0, 1, 0, 0); // Resets transformations

        // Clear canvas every frame to prevent saving dashed lines
        context.clearRect(
            0,
            0,
            annotationCanvas.width,
            annotationCanvas.height,
        );
        context.translate(transX, transY);
    };

    drawLiveLine = (point) => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        this.transformAnnotationCanvas();

        this.drawStaticLines();

        this._closetPolygon.forEach((pt) => {
            CanvasUtils.drawCircle(context, pt, 2);
        });

        const annotationCanvasRect = annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        const tol = this.snapThreshold;
        let showDashedLine = false;
        let snapX = null,
            snapY = null;

        // Snap to last drawn point (your existing logic)
        if (this.currentPoint) {
            const dx = Math.abs(point.x - this.currentPoint.x);
            const dy = Math.abs(point.y - this.currentPoint.y);

            if (dx < tol) point.x = this.currentPoint.x;
            if (dy < tol) point.y = this.currentPoint.y;
        }

        // **New Optimization: Find nearest snapping point using RBush**
        const nearest = this.findNearestPoint(point, tol);
        if (nearest) {
            point.x = nearest.x;
            point.y = nearest.y;
            CanvasUtils.drawCircle(context, point, 4, 'green');
        }

        // If polygon has 3+ points, snap to first point for closure
        if (this._closetPolygon.length >= 3) {
            const firstPoint = this._closetPolygon[0];
            const alignX = Math.abs(point.x - firstPoint.x) < tol;
            const alignY = Math.abs(point.y - firstPoint.y) < tol;

            if (alignX) {
                point.x = firstPoint.x;
                snapX = firstPoint.x;
                showDashedLine = true;
            }
            if (alignY) {
                point.y = firstPoint.y;
                snapY = firstPoint.y;
                showDashedLine = true;
            }
        }

        const color = this._drawingMode === DrawingMode.DRAW ? 'red' : 'orange';

        // Draw the main line
        const points = [this.currentPoint, point];
        CanvasUtils.drawLines(context, points, color);

        // Draw snapping guides
        if (showDashedLine) {
            context.save();
            context.setLineDash([2, 2]);
            context.strokeStyle = 'red';
            context.lineWidth = 1;
            context.beginPath();
            const transX = this._libState.closetManager.translateX;
            const transY = this._libState.closetManager.translateY;
            if (snapX !== null) {
                context.moveTo(snapX, 0);
                context.lineTo(snapX, annotationCanvas.height - transY);
            }
            if (snapY !== null) {
                context.moveTo(0, snapY);
                context.lineTo(annotationCanvas.width - transX, snapY);
            }
            context.stroke();
            context.restore();
        }

        if (this.isCalibrationModeOn) return;
    };

    drawLivePointForCalibration = (point) => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        this.transformAnnotationCanvas();

        const annotationCanvasRect = annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        const tol = this.snapThreshold;

        const nearest = this.findNearestPoint(point, tol);

        if (nearest) {
            point.x = nearest.x;
            point.y = nearest.y;
            CanvasUtils.drawCircle(context, point, 4, 'red');
        } else {
            CanvasUtils.drawCircle(context, point, 4, 'green');
        }
    };

    drawLivePoint = (point) => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        this.transformAnnotationCanvas();

        const annotationCanvasRect = annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        const tol = this.snapThreshold;

        const nearest = this.findNearestPoint(point, tol);
        if (nearest) {
            point.x = nearest.x;
            point.y = nearest.y;
            CanvasUtils.drawCircle(context, point, 4, 'green');
        }
    };

    drawLivePointForAttachments = (point) => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;

        this.transformAnnotationCanvas();

        const annotationCanvasRect = annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;

        const tol = this.snapThreshold;

        const nearest = this._libState.closetManager.nearestClosetPoint(
            point,
            tol,
            false,
        );
        if (nearest) {
            point.x = nearest.x;
            point.y = nearest.y;
            CanvasUtils.drawCircle(context, point, 4, 'blue');
        }
        const edgePoints = this._libState.closetManager.selectedEdgeEnds;
        const pointOnLine = Utils.isPointOnLine(
            point,
            edgePoints[0],
            edgePoints[1],
        );
        if (pointOnLine) {
            const points = [this.currentPoint, point];
            if (!points) return;
            CanvasUtils.drawLines(context, points, 'green', 5);
        }
    };

    drawLiveLineForInternalWall = (point) => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const transX = this._libState.closetManager.translateX;
        const transY = this._libState.closetManager.translateY;
        this.transformAnnotationCanvas();
        const annotationCanvasRect = annotationCanvas.getBoundingClientRect();
        point.x -= annotationCanvasRect.left + transX;
        point.y -= annotationCanvasRect.top + transY;
        const tol = 50;
        const nearest = this._libState.closetManager.nearestClosetPoint(
            point,
            tol,
            true,
        );
        let wallPosition = null;
        let wallAngle = 0;
        let closetId = null;
        if (nearest) {
            closetId =
                this._libState.closetManager.fetchClosetIdByAttachmentCoordinates(
                    [nearest],
                    this._libState.closetManager.selectedPageNo,
                );
            const closet = this._libState.closetManager.closets.find(
                (closet) => closet.id === closetId,
            );
            const edgePoints = this._libState.closetManager.selectedEdgeEnds;
            if (!Utils.isClockwise(closet.coordinates)) {
                edgePoints.reverse();
            }
            const pointOnLine = Utils.isPointOnLine(
                nearest,
                edgePoints[0],
                edgePoints[1],
            );
            if (pointOnLine) {
                const angle = Math.atan2(
                    edgePoints[1].y - edgePoints[0].y,
                    edgePoints[1].x - edgePoints[0].x,
                );
                wallPosition = nearest;
                wallAngle = angle + Math.PI / 2;
            }
        } else {
            for (const closet of this._libState.closetManager.closets) {
                if (
                    closet.pageNo !==
                    this._libState.closetManager.selectedPageNo
                ) {
                    continue;
                }
                closetId = closet.id;
                const closetPolygon = closet.coordinates;
                const isInsideCloset = Utils.pointInPolygon(
                    point,
                    closetPolygon,
                );
                if (isInsideCloset) {
                    wallPosition = point;
                    wallAngle = Math.PI / 2;
                    break;
                }
            }
        }
        if (wallPosition) {
            if (closetId === null) return;
            const scaleFactor =
                this._libState.closetManager.getScaleFactorForAnnotationDisplayByClosetId(
                    this._libState.closetManager.selectedPageNo,
                    closetId,
                    true,
                );
            const width =
                DEFAULT_INTERNAL_WALL_LENGTH *
                scaleFactor *
                SCALEFACTOR_FOR_CONVERSION;
            const height =
                DEFAULT_INTERNAL_WALL_WIDTH *
                scaleFactor *
                SCALEFACTOR_FOR_CONVERSION;
            const centerX = wallPosition.x + (width / 2) * Math.cos(wallAngle);
            const centerY = wallPosition.y + (width / 2) * Math.sin(wallAngle);
            const degrees = wallAngle * (180 / Math.PI);
            const normal = {
                x: Math.cos(wallAngle),
                y: Math.sin(wallAngle),
                z: 0,
            };
            const magnitude = Math.sqrt(
                normal.x * normal.x + normal.y * normal.y,
            );
            normal.x /= magnitude;
            normal.y /= magnitude;
            normal.z = 0;
            const internalWallData = {
                width: width,
                height: height,
                angle: degrees,
                position: { x: centerX, y: centerY },
                closetId: closetId,
                wallNormal: normal,
            };
            this._libState.closetManager.setInternalWallData(internalWallData);
            CanvasUtils.drawRectangleForObstacles(
                context,
                internalWallData.position,
                internalWallData.width,
                internalWallData.height,
                internalWallData.angle,
                'blue',
            );
        }
    };

    drawStaticLines = () => {
        const annotationCanvas = this.annotationCanvas;
        const context = annotationCanvas.getContext('2d');
        const color = this._drawingMode === DrawingMode.DRAW ? 'red' : 'orange';
        CanvasUtils.drawsStaticLines(context, this._closetPolygon, color);
        if (this.isCalibrationModeOn) return;
    };

    stopDrawing = () => {
        this.setLiveLine(false);
        this.clearAnnotationCanvas();
        this.setCurrentPoint(null);
        this._rectangleStartPoint = null;
        this._closetPolygon = [];
        this._libState.closetManager.drawNewClosets();
    };

    get isRectangleModeOn() {
        return this._rectangleMode;
    }

    setRectangleMode = (rectangleModeBool) => {
        if (rectangleModeBool) {
            this._rectangleMode = true;
            this._rectangleStartPoint = null;
            this._closetPolygon = [];
            this.clearAnnotationCanvas();
        } else {
            this._rectangleMode = false;
            this._rectangleStartPoint = null;
            this._closetPolygon = [];
            this.clearAnnotationCanvas();
            this._libState.closetManager.drawNewClosets();
        }
    };

    drawLiveRectangle = (point) => {
        try {
            if (!this._rectangleStartPoint) return;
            const annotationCanvas = this.annotationCanvas;
            const context = annotationCanvas.getContext('2d');
            const transX = this._libState.closetManager.translateX;
            const transY = this._libState.closetManager.translateY;
            this.transformAnnotationCanvas();
            const annotationCanvasRect =
                this._annotationCanvas.getBoundingClientRect();
            const translatedPoint = {
                x: point.x - annotationCanvasRect.left - transX,
                y: point.y - annotationCanvasRect.top - transY,
            };

            const tol = this.snapThreshold;

            this._currentPoint = translatedPoint;

            const nearest = this.findNearestPoint(translatedPoint, tol);
            if (nearest) {
                translatedPoint.x = nearest.x;
                translatedPoint.y = nearest.y;
                CanvasUtils.drawCircle(context, translatedPoint, 4, 'green');
            }

            // Calculate all four corners of the rectangle
            this._closetPolygon = this.calculateRectangleCorners(
                this._rectangleStartPoint,
                translatedPoint,
            ).corners;

            const ctx = this._annotationCanvas.getContext('2d');
            ctx.clearRect(
                0,
                0,
                this._annotationCanvas.width,
                this._annotationCanvas.height,
            );
            ctx.save();

            ctx.strokeStyle = '#f46147';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(this._closetPolygon[0].x, this._closetPolygon[0].y);
            ctx.lineTo(this._closetPolygon[1].x, this._closetPolygon[1].y);
            ctx.lineTo(this._closetPolygon[2].x, this._closetPolygon[2].y);
            ctx.lineTo(this._closetPolygon[3].x, this._closetPolygon[3].y);
            ctx.closePath();
            ctx.stroke();

            const drawSnapCircle = (x, y) => {
                ctx.beginPath();
                ctx.arc(x, y, 8, 0, 2 * Math.PI);
                ctx.fillStyle = '#f46147';
                ctx.fill();
                ctx.strokeStyle = '#ffffff';
                ctx.lineWidth = 1;
                ctx.stroke();
            };

            drawSnapCircle(
                this._rectangleStartPoint.x,
                this._rectangleStartPoint.y,
            );
            ctx.restore();
        } catch (e) {
            console.error('Error in drawLiveRectangle:', e);
        }
    };

    addPointToRectanglePolygonForCloset = (point) => {
        try {
            const transX = this._libState.closetManager.translateX;
            const transY = this._libState.closetManager.translateY;
            const annotationCanvasRect =
                this._annotationCanvas.getBoundingClientRect();
            const translatedPoint = {
                x: point.x - annotationCanvasRect.left - transX,
                y: point.y - annotationCanvasRect.top - transY,
            };

            let snappedPoint = this.findNearestPoint(
                translatedPoint,
                this.snapThreshold,
            );
            if (!snappedPoint) {
                snappedPoint = this.snapPointToPolygon(translatedPoint);
            }

            if (!this._rectangleStartPoint) {
                const isInsideUnit =
                    this._libState.closetManager.checkClosetInsideUnit(
                        [snappedPoint],
                        false,
                    );
                if (!isInsideUnit) {
                    message.error('Start point must be inside a unit');
                    return;
                }
                this._rectangleStartPoint = snappedPoint;
                this._closetPolygon = [snappedPoint];
                return;
            }

            // Calculate all four corners of the rectangle
            const corners = this.calculateRectangleCorners(
                this._rectangleStartPoint,
                snappedPoint,
            );
            const realWidth = corners.realWidth;
            const realHeight = corners.realHeight;
            if (realWidth < 5 || realHeight < 5) {
                message.error('Width or height is too small.');
                return;
            }
            this._closetPolygon = corners.corners;

            const isInsideUnit =
                this._libState.closetManager.checkClosetInsideUnit(
                    this._closetPolygon,
                    false,
                );
            if (!isInsideUnit) {
                message.error('Rectangle must be drawn inside a unit');
                return;
            }

            if (
                this._libState.closetManager.closetIntersectionForIncompleteClosets(
                    this._closetPolygon,
                )
            ) {
                throw new Error(
                    'Rectangle cannot intersect with existing closets',
                );
            }

            const isClosetInside = this._libState.closetManager.closets.some(
                (closet) => {
                    if (
                        closet.pageNo !==
                        this._libState.closetManager.selectedPageNo
                    )
                        return false;
                    let isInside = false;
                    const closetPolygon = closet.coordinates;
                    closetPolygon.forEach((point) => {
                        if (Utils.pointInPolygon(point, this._closetPolygon))
                            isInside = true;
                    });
                    if (isInside) return closet;
                },
            );
            if (isClosetInside) {
                throw new Error(
                    'Rectangle cannot intersect with existing closets',
                );
            }
            this.saveCloset();

            this.setRectangleMode(false);
        } catch (e) {
            message.error(e.message);
        }
    };

    addPointToRectanglePolygonForUnit = (point) => {
        try {
            const transX = this._libState.closetManager.translateX;
            const transY = this._libState.closetManager.translateY;
            const annotationCanvasRect =
                this._annotationCanvas.getBoundingClientRect();
            const translatedPoint = {
                x: point.x - annotationCanvasRect.left - transX,
                y: point.y - annotationCanvasRect.top - transY,
            };

            let snappedPoint = this.findNearestPoint(
                translatedPoint,
                this.snapThreshold,
            );
            if (!snappedPoint) {
                snappedPoint = this.snapPointToPolygon(translatedPoint);
            }

            if (!this._rectangleStartPoint) {
                this._rectangleStartPoint = snappedPoint;
                this._closetPolygon = [snappedPoint];
                return;
            }

            // Calculate all four corners of the rectangle
            const corners = this.calculateRectangleCorners(
                this._rectangleStartPoint,
                snappedPoint,
            );
            const realWidth = corners.realWidth;
            const realHeight = corners.realHeight;
            if (realWidth < 5 || realHeight < 5) {
                message.error('Width or height is too small.');
                return;
            }
            this._closetPolygon = corners.corners;
            const isInsideUnit =
                this._libState.closetManager.checkClosetInsideUnit(
                    this.closetPolygon,
                    false,
                );
            if (isInsideUnit) {
                throw new Error('Rectangle must not be drawn inside a unit');
            }

            if (
                this._libState.closetManager.unitIntersectionForIncompleteUnits(
                    this.closetPolygon,
                )
            ) {
                throw new Error(
                    'Rectangle cannot intersect with existing units',
                );
            }

            const isClosetInside = this._libState.closetManager.units.some(
                (closet) => {
                    if (
                        closet.pageNo !==
                        this._libState.closetManager.selectedPageNo
                    )
                        return false;
                    let isInside = false;
                    const closetPolygon = closet.coordinates;
                    closetPolygon.forEach((point) => {
                        if (Utils.pointInPolygon(point, this._closetPolygon))
                            isInside = true;
                    });
                    if (isInside) return closet;
                },
            );
            if (isClosetInside) {
                throw new Error(
                    'Rectangle cannot encapsulate an existing unit',
                );
            }
            this.saveUnit();

            this.setRectangleMode(false);
        } catch (e) {
            message.error(e.message);
        }
    };

    handleZoomChange = () => {
        if (
            this._rectangleMode &&
            this._rectangleStartPoint &&
            this._currentPoint
        ) {
            this.drawLiveRectangle(this._currentPoint);
        }
    };

    calculateRectangleCorners = (startPoint, endPoint) => {
        const scaleFactor =
            this._libState.closetManager.getScaleForPage(
                this._libState.closetManager.selectedPageNo,
            ) *
            this._libState.closetManager.fetchTransformationScaleFactorByPageNo(
                this._libState.closetManager.selectedPageNo,
            );
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);
        const realWidth = CanvasUtils.measureDistance(
            { x: startPoint.x, y: startPoint.y },
            { x: endPoint.x, y: startPoint.y },
            scaleFactor,
        );
        const realHeight = CanvasUtils.measureDistance(
            { x: startPoint.x, y: startPoint.y },
            { x: startPoint.x, y: endPoint.y },
            scaleFactor,
        );
        const roundedWidth = Math.round(realWidth);
        const roundedHeight = Math.round(realHeight);
        if (realWidth == 0 || realHeight == 0) {
            throw new Error('Width or height is too small.');
        }
        const widthScale = roundedWidth / realWidth;
        const heightScale = roundedHeight / realHeight;

        const dirX = endPoint.x >= startPoint.x ? 1 : -1;
        const dirY = endPoint.y >= startPoint.y ? 1 : -1;
        const corners = [
            { x: startPoint.x, y: startPoint.y },
            {
                x: startPoint.x + width * widthScale * dirX,
                y: startPoint.y,
            },
            {
                x: startPoint.x + width * widthScale * dirX,
                y: startPoint.y + height * heightScale * dirY,
            },
            {
                x: startPoint.x,
                y: startPoint.y + height * heightScale * dirY,
            },
        ];
        return { corners, realWidth, realHeight };
    };

    roundDistanceToWholeNumber = (startPoint, endPoint) => {
        const dx = endPoint.x - startPoint.x;
        const dy = endPoint.y - startPoint.y;

        if (dx !== 0 && dy !== 0) return endPoint;
        const scaleFactor =
            this._libState.closetManager.getScaleForPage(
                this._libState.closetManager.selectedPageNo,
            ) *
            this._libState.closetManager.fetchTransformationScaleFactorByPageNo(
                this._libState.closetManager.selectedPageNo,
            );

        const distance = CanvasUtils.measureDistance(
            startPoint,
            endPoint,
            scaleFactor,
        );

        if (Number.isInteger(distance)) {
            return endPoint;
        }

        const roundedDistance = Math.round(distance);

        const unitX = dx / distance;
        const unitY = dy / distance;

        const newX = startPoint.x + unitX * roundedDistance;
        const newY = startPoint.y + unitY * roundedDistance;

        return { x: newX, y: newY };
    };

    handleDeselectAll = () => {
        this.setDrawingMode(false);
        this.setRectangleMode(false);
        this.stopDrawing();
        this.setDoorMode(false);
        this.setDrawingForm(false);
        this._libState.closetManager.setObstacleForm(false);
        this._libState.closetManager.setObstacleMode(false);
        this._libState.closetManager.setDistanceLabel(true);
    };
}
